import { BigNumber } from '@0x/utils'
import assetUtils, { Asset } from 'app-lib/assetUtils'
import hashUtils from 'app-lib/hashUtils'
import loanUtils from 'app-lib/loanUtils'
import { autoFormat, formatPercent } from 'app-lib/numberUtils'
import { links } from 'bzx-common/src/config/appConfig'
import LoanModel from 'bzx-common/src/stores/BorrowStore/Loan'
import { observer } from 'mobx-react'
import React from 'react'
import ExternalLink from 'shared-components/ExternalLink'
import PriceSwitch from 'shared-components/PriceSwitch'
import TokenLoader from 'shared-components/TokenLoader'
import { Button, ButtonClipboard, Icon, InfoTip } from 'ui-framework'
import GreenFace from '../../../../public/gif/GreenFace.gif'
import RedFace from '../../../../public/gif/RedFace.gif'
import YellowFace from '../../../../public/gif/YellowFace.gif'

const ookiFace: Record<'safe' | 'danger' | 'unsafe', string> = {
  safe: GreenFace,
  danger: RedFace,
  unsafe: YellowFace,
}

/**
 * Returns a twitter intent link for a loan.
 */
function getTweetLink(asset: Asset, rate: BigNumber, loanAmount: BigNumber) {
  const moneyAdjective = rate.lt(1) ? 'Free' : 'Cheap'
  return `https://twitter.com/intent/tweet?original_referer=${encodeURIComponent(
    'https://ooki.com'
  )}&ref_src=twsrc%5Etfw%7Ctwcamp%5Ebuttonembed%7Ctwterm%5Emention%7Ctwgr%5EOokiTrade&screen_name=OokiTrade&text=${encodeURIComponent(
    `I'm borrowing ${autoFormat(loanAmount)} ${asset} at ${formatPercent(
      rate
    )}% interest rate on https://ooki.com/borrow. ${moneyAdjective} Money. 😎💰`
  )}`
}

export function Loan({ loan }: { loan: LoanModel }) {
  const positionSafetyText = loanUtils.getPositionSafetyText(
    loan.loanData.maintenanceMargin,
    loan.collateralizedPercent
  )
  const collateralizedStateSelector =
    positionSafetyText === 'Safe' ? 'safe' : positionSafetyText === 'Danger' ? 'danger' : 'unsafe'
  const loanToken = assetUtils.getCommon(loan.loanAsset)
  const collateralToken = assetUtils.getCommon(loan.collateralAsset)
  const borrowApr = formatPercent(loan.interestRate)

  return (
    <div className="loan panel--simple padded-2 flex-column">
      <div className="title-section flex-row-center margin-bottom-1">
        <div title={loan.loanId} className="loan__header-asset-img">
          <TokenLoader size={38} isLoading={loan.pendingTx}>
            <Icon
              name={loanToken.spriteLogo}
              size={38}
              bgCircle
              bgColor="var(--color-bg-default)"
              bgPadding={1}
            />
          </TokenLoader>
        </div>
        <Icon className="loan__collateral-icon" name={collateralToken.spriteLogo} size={36} />
        <div className="txt-default margin-left-05">
          {loanToken.displayName} / {collateralToken.displayName}
        </div>

        <ExternalLink
          className="link--no-line flex-row-end"
          href={getTweetLink(loan.loanAsset, loan.interestRate, loan.amountOwed)}>
          Share <Icon name="twitter" size={24} title="Share on Twitter" />
        </ExternalLink>
      </div>
      <div className="flex-row justify-between margin-bottom-1">
        <div className="flex-col">
          <div className="txt-subtitle margin-right-1 txt-muted">Amount Borrowed</div>
          <div title={`${loan.amountOwed.toFixed()} ${loan.loanAsset}`} className="txt-title">
            {autoFormat(loan.amountOwed)} <span className="txt-alternate">{loan.loanAsset}</span>
          </div>
        </div>
        <div className="flex-col items-end">
          <div className="txt-subtitle">Collateral</div>
          <div
            title={`${loan.collateralAmount.toFixed(18)} ${loan.collateralAsset}`}
            className="flex-row-center">
            <div className="txt-title">{autoFormat(loan.collateralAmount)}</div>&nbsp;
            <div className="txt-alternate">{collateralToken.displayName}</div>
          </div>
        </div>
      </div>

      <div className="flex-row justify-between margin-bottom-1">
        <div className="flex-col">
          <div className="txt-subtitle margin-right-1 txt-muted">
            Collateralized{' '}
            <InfoTip id="collateral-percent-tip">
              Each asset pairing has unique collateral requirements for position maintenance.{' '}
              <ExternalLink href={links.loanCollateralTable} className="link--discreet">
                Learn More
              </ExternalLink>
              .
            </InfoTip>
          </div>
          {positionSafetyText !== 'Display Error' ? (
            <div
              title={`${loan.collateralizedPercent.multipliedBy(100).plus(100).toFixed(18)}%`}
              className={`loan__body-collateralized txt-title ${collateralizedStateSelector}`}>
              {formatPercent(loan.collateralizedPercent.multipliedBy(100).plus(100))}%
            </div>
          ) : (
            <div className="txt-title">{positionSafetyText}</div>
          )}
        </div>
        <div className="flex-col items-end">
          <div className="txt-subtitle">Borrow. Rate</div>
          <div title={`${autoFormat(loan.interestRate)}% APR`} className="txt-title">
            {borrowApr === '0' && '~'}
            {borrowApr}%
          </div>
        </div>
      </div>
      <div className="flex-row justify-between margin-bottom-1">
        <div className="flex-col">
          <div className="txt-subtitle margin-right-1 txt-muted">Liq. Price</div>
          <div title={`${loan.selectedLiquidationPrice.toFixed()}`} className="txt-title">
            {autoFormat(loan.selectedLiquidationPrice)}
          </div>
          {loan.activeTokenLiquidation && (
            <PriceSwitch
              selectedAsset={loan.activeTokenLiquidation}
              loanAsset={loan.loanAsset}
              collateralAsset={loan.collateralAsset}
              textTooltip="Liq is the price at which your position is liquidated."
              onAssetChange={(activeTokenLiquidation) =>
                loan.set('activeTokenLiquidation', activeTokenLiquidation)
              }
            />
          )}
        </div>
        <div className="txt-right">
          <img
            title={`The loan collaterization is: ${collateralizedStateSelector}`}
            src={ookiFace[collateralizedStateSelector]}
            alt={collateralizedStateSelector}
            width="40px"
          />
          <div>
            <span className="margin-right-05ch" title={loan.loanId}>
              <span className="txt-subtitle">ID</span>:{' '}
              {hashUtils.shortHash(loan.loanId.slice(2), 2, 3)}
            </span>
            <ButtonClipboard text={loan.loanId} top={-4} />
          </div>
        </div>
      </div>

      <hr className="content-separator margin-v-15 width-100p" />
      {loan.isInProgress ? (
        <div className="loan__in-progress-container">
          <div className="loan__in-progress-title">Pending</div>
          <div className="loan__in-progress-animation">{/**/}</div>
        </div>
      ) : (
        <div className="loan__actions-container">
          <Button
            variant="blue-inverse"
            title={loan.isUnhealthyLoan ? 'Collateral too low' : ''}
            disabled={loan.isUnhealthyLoan || loan.pendingTx}
            name="action"
            className="user-loan__borrow-more-btn"
            value="borrow"
            onClick={loan.setActive}
            onClickEmit="name-value">
            Borrow More
          </Button>

          <Button
            variant={loan.isUnhealthyLoan ? 'red-inverse' : 'blue-inverse'}
            name="action"
            value="collateral"
            onClick={loan.setActive}
            onClickEmit="name-value"
            disabled={loan.pendingTx}>
            Collateral
          </Button>

          <Button
            variant="blue-inverse"
            name="action"
            value="repay"
            onClick={loan.setActive}
            onClickEmit="name-value"
            disabled={loan.pendingTx}>
            Repay Loan
          </Button>
        </div>
      )}
    </div>
  )
}

export default observer(Loan)
